<template>
  <div>
    <div class="sticky-board__toolbar">
      <div class="sticky-board__title">{{ $t("sticky.title") }}</div>
      <div class="sticky-board__subtitle">
        {{ $t("sticky.subtitle") }}
      </div>
    </div>

    <div
      ref="board"
      class="sticky-board"
      :style="{ minHeight: `${boardHeight}px` }"
    >
      <div
        v-for="(item, index) in items"
        :key="item.key"
        class="sticky-note"
        :class="{
          'sticky-note--dragging': draggingKey === item.key,
          'sticky-note--teacher': item.fromTeacher,
        }"
        :style="noteStyle(item, index)"
        @mousedown="startDrag(item, $event)"
      >
        <div class="sticky-note__pin"></div>

        <div v-if="item.fromTeacher" class="sticky-note__author">
          <i class="mdi mdi-account-tie-outline"></i>
          <span>{{ item.authorName }}</span>
        </div>

        <!-- Card 86aknav25: o aluno corrige o que escreveu, sem precisar
             apagar e reescrever. O mousedown para aqui: arrastar o sticker
             não pode roubar o clique da edição. -->
        <template v-if="editingKey === item.key">
          <textarea
            ref="editor"
            v-model="editingText"
            class="sticky-note__editor"
            rows="5"
            @mousedown.stop
          ></textarea>
          <div class="sticky-note__actions" @mousedown.stop>
            <button
              type="button"
              class="sticky-note__action"
              :disabled="savingNote"
              @click="cancelEdit"
            >
              {{ $t("common.cancel") }}
            </button>
            <button
              type="button"
              class="sticky-note__action sticky-note__action--save"
              :disabled="savingNote || !editingText.trim()"
              @click="saveEdit(item)"
            >
              {{ $t("common.save") }}
            </button>
          </div>
        </template>

        <template v-else>
          <!-- Link escrito na anotação chega clicável (card 86akj6ghg). -->
          <linkified-text
            tag="div"
            class="sticky-note__body"
            :text="item.note"
          />

          <button
            v-if="!item.fromTeacher"
            type="button"
            class="sticky-note__edit"
            :title="$t('sticky.edit')"
            :aria-label="$t('sticky.edit')"
            @mousedown.stop
            @click="startEdit(item)"
          >
            <i class="mdi mdi-pencil"></i>
          </button>
        </template>

        <!-- Anexos que o professor mandou junto com a anotação. O mousedown
             para aqui: arrastar o sticker não pode roubar o clique do link. -->
        <ul v-if="item.files.length" class="sticky-note__files">
          <li v-for="file in item.files" :key="file.id">
            <a
              :href="file.url"
              target="_blank"
              rel="noopener"
              :title="file.name"
              @mousedown.stop
            >
              <i class="mdi mdi-paperclip"></i>
              {{ file.name }}
            </a>
          </li>
        </ul>
      </div>

      <div v-if="!items.length" class="sticky-board__empty">
        {{ $t("sticky.empty") }}
      </div>
    </div>
  </div>
</template>

<script>
import LinkifiedText from "@/components/course/LinkifiedText.vue";
const NOTE_WIDTH = 220;
const NOTE_HEIGHT = 180;
const BOARD_PADDING = 24;
const NOTE_COLORS = [
  { background: "#fff4a8", shadow: "rgba(217, 119, 6, 0.2)", rotate: "-2deg" },
  { background: "#ffd6e7", shadow: "rgba(190, 24, 93, 0.18)", rotate: "1deg" },
  { background: "#d8f3dc", shadow: "rgba(22, 163, 74, 0.16)", rotate: "-1deg" },
  { background: "#dbeafe", shadow: "rgba(37, 99, 235, 0.16)", rotate: "2deg" },
];
// Sticker do professor tem paleta propria para nao se confundir com os do aluno.
const TEACHER_COLORS = [
  { background: "#ede9fe", shadow: "rgba(109, 40, 217, 0.22)", rotate: "1deg" },
  { background: "#e0f2fe", shadow: "rgba(2, 132, 199, 0.2)", rotate: "-1deg" },
];

export default {
  name: "StickyNotesBoard",
  components: { LinkifiedText },

  props: {
    // Anotacoes do proprio aluno: [{ id, note }]
    notes: {
      type: Array,
      default: () => [],
    },
    // Anotacoes recebidas do professor: [{ id, note, author }] - somente leitura
    teacherNotes: {
      type: Array,
      default: () => [],
    },
    moduleKey: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      boardHeight: 360,
      layouts: {},
      draggingKey: null,
      dragContext: null,
      // Edição da própria anotação (card 86aknav25).
      editingKey: null,
      editingText: "",
      savingNote: false,
    };
  },

  computed: {
    // Os ids vem de tabelas diferentes e colidem entre si, entao cada sticker
    // recebe uma chave prefixada pela origem. E ela que indexa o layout salvo.
    items() {
      const own = this.notes
        .filter((note) => note && note.id)
        .map((note) => ({
          key: `own:${note.id}`,
          note: note.note,
          fromTeacher: false,
          authorName: "",
          // Anotação do próprio aluno não tem anexo — só a do professor tem.
          files: [],
        }));

      const fromTeacher = this.teacherNotes
        .filter((note) => note && note.id)
        .map((note) => ({
          key: `teacher:${note.id}`,
          note: note.note,
          fromTeacher: true,
          authorName:
            (note.author && note.author.name) || this.$t("sticky.yourTeacher"),
          files: note.files || [],
        }));

      return [...fromTeacher, ...own];
    },
  },

  watch: {
    items: {
      deep: true,
      immediate: true,
      handler() {
        this.syncLayouts();
      },
    },
  },

  beforeDestroy() {
    this.removeListeners();
  },

  methods: {
    defaultLayout(index, fromTeacher) {
      const palette = fromTeacher
        ? TEACHER_COLORS[index % TEACHER_COLORS.length]
        : NOTE_COLORS[index % NOTE_COLORS.length];

      return {
        x: BOARD_PADDING + (index % 3) * (NOTE_WIDTH + 18),
        y: BOARD_PADDING + Math.floor(index / 3) * (NOTE_HEIGHT + 18),
        background: palette.background,
        shadow: palette.shadow,
        rotate: palette.rotate,
      };
    },
    storageKey() {
      return `pitch-sticky-notes:${this.moduleKey}`;
    },
    readStoredLayouts() {
      try {
        return JSON.parse(localStorage.getItem(this.storageKey()) || "{}");
      } catch (error) {
        return {};
      }
    },
    // Antes desta versao o layout era indexado pelo id cru da nota do aluno.
    // Preserva o que ja estava salvo para quem havia organizado o mural.
    legacyLayoutFor(stored, item) {
      if (item.fromTeacher) return null;
      const legacyId = item.key.slice("own:".length);
      return stored[legacyId] || null;
    },
    saveLayouts() {
      localStorage.setItem(this.storageKey(), JSON.stringify(this.layouts));
    },
    syncLayouts() {
      const storedLayouts = this.readStoredLayouts();
      const nextLayouts = {};

      this.items.forEach((item, index) => {
        const fallbackLayout = this.defaultLayout(index, item.fromTeacher);
        const stored =
          storedLayouts[item.key] ||
          this.legacyLayoutFor(storedLayouts, item) ||
          {};

        nextLayouts[item.key] = {
          x: typeof stored.x === "number" ? stored.x : fallbackLayout.x,
          y: typeof stored.y === "number" ? stored.y : fallbackLayout.y,
          background: stored.background || fallbackLayout.background,
          shadow: stored.shadow || fallbackLayout.shadow,
          rotate: stored.rotate || fallbackLayout.rotate,
        };
      });

      this.layouts = nextLayouts;
      this.saveLayouts();
      this.updateBoardHeight();
    },
    noteStyle(item, index) {
      const layout =
        this.layouts[item.key] || this.defaultLayout(index, item.fromTeacher);

      return {
        width: `${NOTE_WIDTH}px`,
        minHeight: `${NOTE_HEIGHT}px`,
        transform: `translate(${layout.x}px, ${layout.y}px) rotate(${layout.rotate})`,
        background: layout.background,
        boxShadow: `0 20px 30px -20px ${layout.shadow}`,
      };
    },
    startEdit(item) {
      this.editingKey = item.key;
      this.editingText = item.note || "";
    },
    cancelEdit() {
      this.editingKey = null;
      this.editingText = "";
    },
    async saveEdit(item) {
      const text = this.editingText.trim();
      if (!text || text === item.note) {
        this.cancelEdit();
        return;
      }

      this.savingNote = true;

      try {
        // Quem fala com a API é a tela: o mural é usado pelo aluno e serve
        // também às anotações do professor, que aqui são só leitura.
        await this.$emit("update-note", {
          id: String(item.key).replace(/^own:/, ""),
          note: text,
        });
        this.cancelEdit();
      } finally {
        this.savingNote = false;
      }
    },
    startDrag(item, event) {
      if (event.button !== 0 || this.editingKey === item.key) {
        return;
      }

      const boardRect = this.$refs.board?.getBoundingClientRect();
      const layout = this.layouts[item.key];

      if (!boardRect || !layout) {
        return;
      }

      this.draggingKey = item.key;
      this.dragContext = {
        key: item.key,
        offsetX: event.clientX - boardRect.left - layout.x,
        offsetY: event.clientY - boardRect.top - layout.y,
      };

      window.addEventListener("mousemove", this.onDrag);
      window.addEventListener("mouseup", this.stopDrag);
    },
    onDrag(event) {
      if (!this.dragContext || !this.$refs.board) {
        return;
      }

      const boardRect = this.$refs.board.getBoundingClientRect();
      const maxX = Math.max(boardRect.width - NOTE_WIDTH - BOARD_PADDING, 0);
      const nextX = Math.min(
        Math.max(event.clientX - boardRect.left - this.dragContext.offsetX, 0),
        maxX
      );
      const nextY = Math.max(
        event.clientY - boardRect.top - this.dragContext.offsetY,
        0
      );
      const layout = this.layouts[this.dragContext.key];

      if (!layout) {
        return;
      }

      this.$set(this.layouts, this.dragContext.key, {
        ...layout,
        x: nextX,
        y: nextY,
      });

      this.updateBoardHeight();
    },
    stopDrag() {
      if (!this.dragContext) {
        return;
      }

      this.draggingKey = null;
      this.dragContext = null;
      this.saveLayouts();
      this.removeListeners();
    },
    removeListeners() {
      window.removeEventListener("mousemove", this.onDrag);
      window.removeEventListener("mouseup", this.stopDrag);
    },
    updateBoardHeight() {
      const layouts = Object.values(this.layouts);

      if (!layouts.length) {
        this.boardHeight = 360;
        return;
      }

      const tallestPoint = layouts.reduce((max, item) => {
        return Math.max(max, (item?.y || 0) + NOTE_HEIGHT + BOARD_PADDING);
      }, 0);

      this.boardHeight = Math.max(tallestPoint, 360);
    },
  },
};
</script>

<style scoped>
.sticky-board__toolbar {
  margin-bottom: 1rem;
}

.sticky-board__title {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--dk-ink, #111827);
}

.sticky-board__subtitle {
  margin-top: 0.2rem;
  color: rgba(31, 41, 55, 0.68);
}

.sticky-board {
  position: relative;
  overflow: hidden;
  border-radius: 28px;
  border: 1px dashed rgba(236, 72, 153, 0.24);
  background: radial-gradient(
      circle at 20% 20%,
      rgba(255, 255, 255, 0.65),
      transparent 30%
    ),
    linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.96) 0%,
      rgba(251, 207, 232, 0.18) 100%
    );
}

.sticky-note {
  position: absolute;
  top: 0;
  left: 0;
  padding: 1.25rem 1rem 1rem;
  border-radius: 18px;
  cursor: grab;
  user-select: none;
  transition: box-shadow 0.18s ease, transform 0.18s ease;
  border: 1px solid rgba(15, 23, 42, 0.08);
}

.sticky-note--teacher {
  border: 1px solid rgba(109, 40, 217, 0.32);
}

.sticky-note--dragging {
  cursor: grabbing;
  z-index: 4;
}

.sticky-note__pin {
  position: absolute;
  top: 12px;
  right: 14px;
  width: 14px;
  height: 14px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.68);
  box-shadow: 0 6px 12px -6px rgba(15, 23, 42, 0.45);
}

.sticky-note--teacher .sticky-note__pin {
  background: #6d28d9;
}

.sticky-note__author {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  margin-bottom: 0.45rem;
  padding-right: 1.5rem;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: #6d28d9;
}

.sticky-note__body {
  white-space: pre-wrap;
  color: var(--dk-ink, #1f2937);
  font-weight: 600;
  line-height: 1.45;
}

/* edição da própria anotação (card 86aknav25) */
.sticky-note__edit {
  position: absolute;
  right: 8px;
  bottom: 8px;
  width: 26px;
  height: 26px;
  display: grid;
  place-items: center;
  border: none;
  border-radius: 8px;
  background: rgba(31, 41, 55, 0.08);
  color: var(--dk-ink, #1f2937);
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.15s ease, background 0.15s ease;
}
.sticky-note:hover .sticky-note__edit,
.sticky-note__edit:focus-visible {
  opacity: 1;
}
.sticky-note__edit:hover {
  background: rgba(31, 41, 55, 0.16);
}

.sticky-note__editor {
  width: 100%;
  flex: 1 1 auto;
  resize: none;
  border: none;
  border-radius: 8px;
  padding: 6px;
  background: rgba(255, 255, 255, 0.65);
  color: var(--dk-ink, #1f2937);
  font: inherit;
  font-weight: 600;
  line-height: 1.45;
  outline: none;
}

.sticky-note__actions {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
  margin-top: 6px;
}
.sticky-note__action {
  border: none;
  border-radius: 8px;
  padding: 4px 10px;
  background: rgba(31, 41, 55, 0.08);
  color: var(--dk-ink, #1f2937);
  font-size: 0.72rem;
  font-weight: 700;
  cursor: pointer;
}
.sticky-note__action--save {
  background: var(--dk-ink, #1f2937);
  color: #fff;
}
.sticky-note__action:disabled {
  opacity: 0.5;
  cursor: default;
}

.sticky-board__empty {
  min-height: 280px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 2rem;
  color: rgba(31, 41, 55, 0.58);
}

@media (max-width: 700px) {
  .sticky-note {
    width: calc(100% - 32px) !important;
    min-height: 160px !important;
  }
}
.sticky-note__files {
  list-style: none;
  margin: 6px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 46px;
  overflow-y: auto;
}
.sticky-note__files a {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  font-size: 0.7rem;
  color: var(--dk-pink-deep, #6b1233);
  text-decoration: underline;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
